<script setup lang="ts" generic="t">
import { computed, ref } from 'vue';
import type { Ref } from 'vue';
import Tree from './PlanroomTree.vue';
import TreeItem from './PlanroomTreeItem.vue';
import { watch } from 'vue';

const props = defineProps<{
  item: t;
  parent: t | null;
  itemKey: keyof t;
  childrenKey: keyof t;
  defaultExpandedState: boolean;
  selectable: boolean;
  depth: number;
}>();
const expanded = defineModel<boolean>('expanded', { default: false });
const selectedItems = defineModel<t[]>('selectedItems', { default: [] });

const subTree = ref();

const children = computed<Array<t>>(() => {
  return props.item[props.childrenKey] as Array<t>;
});

const getInitialSelectedChildren = () => {
  return selectedItems.value.filter((item) =>
    props.item[props.childrenKey].includes(item),
  );
};

const selectedChildren = ref(getInitialSelectedChildren()) as Ref<Array<t>>;
const subTreeParentSelections = ref(new Map()) as Ref<Map<t, boolean | null>>;

const allChildrenSelected = computed(() => {
  return (
    children.value?.filter(
      (child) => (child[props.childrenKey]?.length ?? 0) === 0,
    ).length === selectedChildren.value.length
  );
});

const allParentsSelected = computed(() => {
  return (
    subTreeParentSelections.value.size === 0 ||
    Array.from(subTreeParentSelections.value.values()).some((val) => val)
  );
});

const someParentsSelected = computed(() => {
  return Array.from(subTreeParentSelections.value.values()).some(
    (val) => val || val === null,
  );
});

const someChildrenSelected = computed(() => {
  return selectedChildren.value.length > 0 || allChildrenSelected.value;
});

const selected = defineModel<boolean | null>('selected', { default: false });

watch(
  [
    allChildrenSelected,
    someChildrenSelected,
    someParentsSelected,
    allParentsSelected,
    selectedItems,
  ],
  () => {
    if (allChildrenSelected.value && allParentsSelected.value) {
      selected.value = true;
    } else if (someChildrenSelected.value || someParentsSelected.value) {
      selected.value = null;
    } else {
      selected.value = false;
    }
  },
  { immediate: true },
);

const resetChildSelections = () => {
  subTree.value?.resetSelection();
};

const setChildrenSelected = (val: boolean | null) => {
  subTree.value?.setParentsSelected(val);
  subTree.value?.setAllItemsSelected(val);
};

const updateSelectedChildren = (item: t, val: boolean) => {
  const idx = selectedChildren.value.findIndex(
    (selectedItem) => item === selectedItem,
  );
  if (val && idx === -1) {
    return selectedChildren.value.push(item);
  }
  if (!val && idx !== -1) {
    return selectedChildren.value.splice(idx, 1);
  }
};

const updateSubTreeParentSelections = (val: Map<t, boolean | null>) => {
  subTreeParentSelections.value = val;
};

const setAllExpanded = (val: boolean) => {
  expanded.value = val;
  subTree.value.setAllExpanded(val);
};

watch(selected, () => {
  if (selected.value === null) return;
  setChildrenSelected(selected.value);
});

const getAllSelectedItems = () => {
  return subTree.value.allSelectedItems ?? [];
};

const allSelectedItems = computed(() => {
  return subTree.value.allSelectedItems;
});

const draggedOver = ref(false);

const dragOver = () => {
  draggedOver.value = true;
};
const dragLeave = () => {
  draggedOver.value = false;
};

const emit = defineEmits<{
  itemMovedToParent: [{ item: t; sourceParent: t; newParent: t }];
}>();

const drop = (e: DragEvent) => {
  draggedOver.value = false;
  if (!e.dataTransfer) return;
  const moveData = JSON.parse(e.dataTransfer.getData('text'));
  emit('itemMovedToParent', {
    item: moveData.item,
    sourceParent: moveData.parent,
    newParent: props.item,
  });
};

defineExpose({
  setChildrenSelected,
  setAllExpanded,
  resetChildSelections,
  item: props.item,
  getAllSelectedItems,
  allSelectedItems,
});
</script>
<template>
  <TreeItem
    v-model:expanded="expanded"
    :class="{ 'tree-parent': true, draggedOver }"
    :item="item"
    :selected="selected"
    :selectable="selectable"
    :parent="parent"
    :depth="depth"
    @update:selected="setChildrenSelected"
    @dragover.prevent="dragOver"
    @dragleave="dragLeave"
    @drop.stop="drop"
  >
    <template #item="childItem">
      <slot name="item" v-bind="childItem"></slot>
    </template>
  </TreeItem>
  <Tree
    v-show="expanded"
    ref="subTree"
    :items="children"
    :item-key="itemKey"
    :children-key="childrenKey"
    :root-item="item"
    :depth="depth + 1"
    :default-expanded-state="false"
    :selected-items="selectedItems"
    :selectable="selectable"
    @child-selected="updateSelectedChildren"
    @update:parent-selections="updateSubTreeParentSelections"
  >
    <template #item="childItem">
      <slot name="item" v-bind="childItem"></slot>
    </template>
  </Tree>
</template>
